import React, { useState, useRef, createContext, useEffect } from 'react'
import { WidgetTypes } from '../common/constants'
import { KatalMetricsPublisher } from '../utils/metrics'

interface LaunchModalContext {
  activeIndex: number
  isPlaying: boolean
  dataTaktId: string
  slidesLength: number
  goToSlide: (...args: any[]) => any
  previousSlide: (...args: any[]) => any
  nextSlide: (...args: any[]) => any
  stopFn: (...args: any[]) => any
  playFn: (...args: any[]) => any
}

export const LaunchModalContext = createContext<LaunchModalContext>({
  activeIndex: 0,
  isPlaying: true,
  dataTaktId: '',
  slidesLength: 1,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  goToSlide: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  previousSlide: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  nextSlide: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  stopFn: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  playFn: () => {},
})

const desktopMetricsPublisher = KatalMetricsPublisher.newChildActionPublisherForMethod(WidgetTypes.DesktopLaunchModal)

const mobileMetricsPublisher = KatalMetricsPublisher.newChildActionPublisherForMethod(WidgetTypes.MobileLaunchModal)

export const LaunchModalContextProvider: React.FC<{ slidesLength: number; slidesDuration: number; isMobile?: boolean; dataTaktId: string }> = ({
  children,
  slidesLength,
  slidesDuration,
  isMobile = false,
  dataTaktId,
}) => {
  const metricsPublisher = isMobile ? mobileMetricsPublisher : desktopMetricsPublisher
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const playTimer: { current: NodeJS.Timeout | undefined } = useRef(undefined)

  useEffect(() => {
    playTimer.current = setTimeout(() => playFn(), 500)
    return () => {
      if (playTimer.current) {
        clearTimeout(playTimer.current)
        playTimer.current = undefined
      }
    }
  }, [])

  const goToSlide = (index) => {
    metricsPublisher.publishCounterMonitor(`goToSlide-${index}`, 1)
    stopFn()
    setActiveIndex(index)
  }

  const nextSlide = (stop = false) => {
    metricsPublisher.publishCounterMonitor(`nextSlide`, 1)
    if (stop) {
      stopFn()
    }

    setActiveIndex((activeIndex) => {
      if (activeIndex + 1 >= slidesLength) {
        stopFn()
        setIsPlaying(false)
        return slidesLength - 1
      } else {
        if (!stop) playTimer.current = setTimeout(() => nextSlide(false), slidesDuration)
        return activeIndex + 1
      }
    })
  }

  const previousSlide = () => {
    metricsPublisher.publishCounterMonitor(`previousSlide`, 1)
    stopFn()
    setActiveIndex(activeIndex - 1)
  }

  const playFn = () => {
    metricsPublisher.publishCounterMonitor(`start`, 1)
    setIsPlaying(true)
    playTimer.current = setTimeout(() => nextSlide(false), slidesDuration)
  }

  const stopFn = () => {
    metricsPublisher.publishCounterMonitor(`stop`, 1)
    if (playTimer.current) {
      clearTimeout(playTimer.current)
      playTimer.current = undefined
    }
    setIsPlaying(false)
  }

  return <LaunchModalContext.Provider value={{ activeIndex, isPlaying, dataTaktId, slidesLength, goToSlide, previousSlide, nextSlide, stopFn, playFn }}>{children}</LaunchModalContext.Provider>
}

export const useLaunchModalContext = (): LaunchModalContext => {
  const context = React.useContext(LaunchModalContext)
  if (!context) {
    throw new Error('useLaunchModalContext must be used within a LaunchModalContext.Provider')
  }
  return context
}
